import BCMSRecordProperty from "./src/BCMSRecordProperty";
import BCMSRecordCRUD from "./src/BCMSRecordCRUD";
import BCMSDefaultModel from "../BCMSDefaultModel";

export default class BCMSRecord {

    static _GlobalController;   // Used for performing CRUD operations on model
    static _RecordType;         // Used for querying model using global controller
    static _RecordIDProp;       // Used by "initByID" static method to return record by ID
    static _RecordProperties;    // Stores array of bcmsRecordProperty instances that define the record we are abstracting the class for
    static _ClassName; // Stores the name of the class, this is required because minimizing the code overwrites the class name

    // CONSTURCTOR :: OBJECT -> this
    // NOTE: Throws an error if _RecordProperties is undefined
    constructor(record) {
        if (this.constructor._RecordProperties instanceof Array) {
            this.constructor._RecordProperties.reduce((result, property) => {
                if (property instanceof BCMSRecordProperty) {
                    return property.initRecordFrom(record, result);
                } else {
                    throw new Error(`Can only initalize an instance of ${this.constructor.name} using instances of BCMSRecordProperty!`);
                }
            }, this);
        } else {
            throw new Error(`_RecordProperties must be set to initalize an instance of ${this.constructor.name}!`);
        }
    }
    
    /**
     * 
     * Getters
     * 
     */

    // :: VOID -> OBJECT
    // Returns instance as an OBJECT that can be stored in model:
    get record () {
        return this.constructor._RecordProperties.reduce((result, property) => {
            return property.initRecordTo(result, this);
        }, {});
    }

    /**
     * 
     * Setters
     * 
     */

    /**
     * 
     *  Static Methods
     *  
     */

    // :: STRING, STRING, [bcmsRecordProperty] -> OBJECT
    // Binds static properties, setters, and getters, to class:
    // NOTE: This should be called whenever extending a class of BCMSRecord
    static define({recordType, recordIDProp, recordProperties, className}) {
        this._RecordType = recordType;
        this._RecordIDProp = recordIDProp;
        this._RecordProperties = recordProperties;
        this._ClassName = className;

        return this;
    }

     // STATIC FACTORY :: OBJECT -> bcmsRecord
     static init(record) {
        return new this(record);
    }

    // :: [OBJECT] -> [bcmsRecord]
    // Initalizes "many" instances from an array of record:
    static initMany(records) {
        return records.map((record) => new this(record));
    }
    
    // ::  OBJECT -> VOID
    // Sets globalController for class
    // NOTE: This MUST be set before using any model CRUD operations
    static globalController(globalController) {
        this._GlobalController = globalController;
        return this;
    }

    // :: ARRAY, OBJECT -> VOID
    // Set the global controller for the array of model classes
    // passed to the function
    static globalControllerFor(recordClasses, globalController) {
        [ ...recordClasses, BCMSDefaultModel ].forEach(item => {
            item.globalController(globalController);
        });
        // Use the classes passed to create the object that will be passed
        // to the setDefaults for the BCMSDefaultModel class
        const classes = recordClasses.reduce((result, item) => ({ ...result, [item._ClassName] : item }), {});
        BCMSDefaultModel.setDefaults(classes);
    }

    // :: STRING|NUMBER -> this|VOID
    // Returns instance of record for given ID:
    // NOTE: Returned undefined if no record is found for given ID:
    static initByID(id) {
        if (this._GlobalController === undefined) {
            throw new Error(`Global controller is undefined for "${this.constructor.name}" class`);
        }
        const record = this.read(id);        
        return record !== undefined 
            ? this.init(record) 
            : undefined;
    }

    // :: VOID -> this|VPOID
    // Returns instnace of first record:
    // NOTE Returns undefined if no record is found:
    static firstInstanceOf() {
        let record = this._GlobalController.state[this._RecordType][0]
        return record !== undefined ? this.init(record) : undefined
    }

    // :: VOID] -> [bcmsRecord]
    // Initalizes all records in model for stored record type:
    static initAll() {
        return this._GlobalController.state[this._RecordType].map((record) => {
            return this.init(record);
        });
    }

    // update :: OBJECT|BCMSRecord -> BCMSRecordCRUD
    // Initalize and add an update operation to update the model, return the new instance of BCMSRecordCRUD
    static update(arg) {
        const id = arg instanceof BCMSRecord ? arg.id : arg[this._RecordIDProp];
        const record = arg instanceof BCMSRecord ? arg.record : arg;

        return BCMSRecordCRUD.init(this._GlobalController).update(this._RecordType, this._RecordIDProp, id, record);
    }

    // create :: OBJECT|BCMSRecord -> BCMSRecordCRUD
    // Initialize and add a create operation to update the model, return the new instance of BCMSRecordCRUD
    static create(arg) {
        const record = arg instanceof BCMSRecord ? arg.record : arg;
        const hasID = record[this._RecordIDProp] !== undefined && this.idExists(record[this._RecordIDProp]);
        const crud = BCMSRecordCRUD.init(this._GlobalController);

        try {
            if(hasID) { throw new Error(`Cannot create ${ this._RecordType } with an existing id of ${ record[this._RecordIDProp] }`); }

            record[this._RecordIDProp] = this.nextID(record[this._RecordIDProp]);
            crud.create(this._RecordType, record);
        } catch(e) { console.log(e); }

        return crud;
    }

    // delete :: INTEGER|STRING -> BCMSRecordCRUD
    // Initalize and add a delete operation to the update the model, return the new instance of BCMSRecordCRUD
    static delete(id) {
        return BCMSRecordCRUD.init(this._GlobalController).delete(this._RecordType, this._RecordIDProp, id);
    }

    // read :: INTEGER|STRING -> OBJECT
    // Initalize a new BCMSRecordCRUD then return the result of the 'read' function
    static read(id) {
        return BCMSRecordCRUD.init(this._GlobalController).read(this._RecordType, this._RecordIDProp, id);
    }

    // batch :: [BCMSRecordCRUD], BCMSController -> VOID
    // Call the 'batch' function of the BCMSRecordCRUD class passing the 'instances' and 'controller' parmeters
    static batch(instances, controller, callback) {
        BCMSRecordCRUD.batch(instances, controller, callback);
    }

    // nextID :: INTEGER -> INTEGER
    // Get the next greatest id from going through the array checking existing ids
    // NOTE: if the 'startingID' property is not set, the starting id will be set to 0
    static nextID(startingID){
        return this._GlobalController.state[this._RecordType].reduce((id, record) => {
            return parseInt(record[this._RecordIDProp]) >= id ? parseInt(record[this._RecordIDProp]) + 1 : id;
        }, startingID !== undefined ? parseInt(startingID) : 0).toString();
    }

    static idExists(id){
        return this._GlobalController.state[this._RecordType].find(record => record[this._RecordIDProp] == id) !== undefined;
    }
}